import type { ILojaConfig } from "interfaces/loja";
import { cnpjApplyMask } from "helpers/utils";

export function formatCEP(value: string): string {
  const digits = String(value || "")
    .replace(/\D/g, "")
    .slice(0, 8);
  return digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
}

export function formatCNPJInput(value: string): string {
  const digits = String(value || "")
    .replace(/\D/g, "")
    .slice(0, 14);
  if (digits.length === 14) return cnpjApplyMask(digits);
  return digits;
}

export function applyLoadedMasks(data: ILojaConfig): ILojaConfig {
  const formatted = { ...data };
  if (formatted.cnpj) {
    const cnpjDigits = String(formatted.cnpj).replace(/\D/g, "");
    if (cnpjDigits.length === 14) formatted.cnpj = cnpjApplyMask(cnpjDigits);
  }
  if (formatted.cep) {
    const cepDigits = String(formatted.cep).replace(/\D/g, "");
    if (cepDigits.length === 8) formatted.cep = formatCEP(cepDigits);
  }
  return formatted;
}

export const EMPTY_LOJA_CONFIG: ILojaConfig = {
  id: "",
  retornarEstoqueCancelado: true,
  createdAt: "",
  updatedAt: "",
};

function pctFilled(fields: (string | null | undefined)[]): number {
  if (!fields.length) return 0;
  const filled = fields.filter((f) => String(f ?? "").trim()).length;
  return Math.round((filled / fields.length) * 100);
}

export function computeConfigStats(config: ILojaConfig | null) {
  if (!config) {
    return {
      empresaPct: 0,
      enderecoOk: false,
      contatoOk: false,
      estoqueAuto: false,
    };
  }

  const empresaPct = pctFilled([
    config.nomeLoja,
    config.cnpj,
    config.razaoSocial,
    config.nomeFantasia,
  ]);

  const enderecoOk = Boolean(
    config.cep?.replace(/\D/g, "").length === 8 &&
      config.logradouro?.trim() &&
      config.numero?.trim() &&
      config.cidade?.trim() &&
      config.uf?.trim(),
  );

  const contatoOk = Boolean(
    config.phoneContato?.trim() && config.emailContato?.trim(),
  );

  return {
    empresaPct,
    enderecoOk,
    contatoOk,
    estoqueAuto: Boolean(config.retornarEstoqueCancelado),
  };
}

export function validateConfig(config: ILojaConfig): { ok: true } | { ok: false; msg: string } {
  if (config.cnpj) {
    const cnpjDigits = config.cnpj.replace(/\D/g, "");
    if (cnpjDigits.length !== 14) {
      return { ok: false, msg: "CNPJ deve conter 14 dígitos." };
    }
  }

  if (config.cep) {
    const cepDigits = config.cep.replace(/\D/g, "");
    if (cepDigits.length < 8) {
      return { ok: false, msg: "CEP do endereço inválido." };
    }
  }

  const emailContato = config.emailContato?.trim();
  if (emailContato && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailContato)) {
    return { ok: false, msg: "E-mail de contato inválido." };
  }

  return { ok: true };
}

export function buildSavePayload(config: ILojaConfig) {
  return {
    id: config.id,
    nomeLoja: config.nomeLoja ?? null,
    cnpj: config.cnpj ? config.cnpj.replace(/\D/g, "") : null,
    inscricaoEstadual: config.inscricaoEstadual ?? null,
    inscricaoMunicipal: config.inscricaoMunicipal ?? null,
    nomeFantasia: config.nomeFantasia ?? null,
    razaoSocial: config.razaoSocial ?? null,
    logradouro: config.logradouro ?? null,
    numero: config.numero ?? null,
    complemento: config.complemento ?? null,
    bairro: config.bairro ?? null,
    cidade: config.cidade ?? null,
    uf: config.uf ?? null,
    cep: config.cep ? config.cep.replace(/\D/g, "") : null,
    pais: config.pais ?? null,
    phoneContato: config.phoneContato?.trim() || null,
    emailContato: config.emailContato?.trim() || null,
    retornarEstoqueCancelado: Boolean(config.retornarEstoqueCancelado),
  };
}

export function getFieldErrors(config: ILojaConfig | null) {
  const cep = config?.cep || "";
  const cepDigits = cep.replace(/\D/g, "");
  const cepInvalido = !!cep && cepDigits.length < 8;

  const cnpj = config?.cnpj || "";
  const cnpjDigits = cnpj.replace(/\D/g, "");
  const cnpjInvalido = !!cnpj && cnpjDigits.length !== 14;

  return { cepInvalido, cnpjInvalido };
}
